import _ from "lodash/fp";
import { BehaviorSubject } from "rxjs";
import { maskTypename } from "@urql/core";

import {
  canSave,
  getAssetInfo,
  getTasksByObjects,
  uniqueObjects,
  updateTasks,
} from "../../services/utils";
import {
  removeNewlineAndTab,
  adjustMathMLSpacing,
  removePTagFromFoils,
  adjustTableFormattingForAPI,
  adjustTableFormattingForUI,
  convertUTF8ToHTMLEntities,
  encodeMathMLTags,
  getCharacterCount,
  getHasUnresolvedChangeTags,
  getTextToSpeechTags,
  removeClassMsoNormalAttr,
  removeFontFamilyFromStyle,
  removeMarkDataSuggestion,
  removeXMLNSAttributesFromMathML,
  unwrapElements,
  wrapPlainTextWithP,
  removeCdeIds,
  removeLabelfromTTS,
  removeDivForInteractions,
  removeFontWeight400FromStyle,
} from "../../services/contentUtils";

import {
  dataSourceOptions,
  relationshipTypeOptions,
} from "../../routes/Workspace/MetadataCard/constants";
import { applyOptimisticRetryStatuses } from "../../providers/TTSFileStatusProvider";

const subject = new BehaviorSubject(null);
let client;

const store = {
  client,
  /**
   * Fetch data from the graphQL API and write the response to the stream
   *
   * @param {string} id
   */
  fetch: (users) => (programs) => async (id) => {
    // If this was called without a value for the ID, send a null to the stream and exit
    if (!id) {
      return subject.next(null);
    }

    try {
      // Query the graphQL API
      const promises = [
        client.query(getItemQuery, { id }).toPromise(),
        getTasksByObjects(client)({ id, type: "item" }),
      ];
      const [response, tasks] = await Promise.all(promises);

      // Throw an error if one is returned
      if (response.error) {
        throw new Error(response.error);
      }

      // Push the response onto the stream (even if nothing was returned)
      if (response.data?.itemByID) {
        const item = await convertToUIStructure(users)(programs)({
          ...response.data.itemByID,
        });
        subject.next({ ...item, tasks });
      } else {
        subject.next(null);
      }
    } catch (err) {
      console.error(err);
    }
  },
  /**
   * Gets the current value of the stream
   */
  get: () => {
    return subject.getValue();
  },
  init: (c) => {
    client = c;
  },
  /**
   * Saves the item data to the server, then puts the response in the stream
   */
  save:
    (users) =>
    (programs) =>
    (notes) =>
    ({ activeTasks }) =>
    (validTypes) =>
    (isTTSToogled) =>
    (ttsTagIdsToRegenerate) =>
    async (data) => {
      if (!data?.id) return Promise.resolve();

      try {
        const { tasks = [], ...rest } = data;
        const variables = convertToAPIStructure(users)(validTypes)(rest);

        // Query the graphQL API
        const updatedTasks = activeTasks.item
          ? await updateTasks(client)(tasks)
          : tasks;
        const response = canSave("item")({
          actions: data._actions,
          activeTasks,
        })
          ? await client
              .mutation(updateItemMutation, {
                id: variables.id,
                data: maskTypename(variables.data),
                notes: notes,
                ttsGenerate: isTTSToogled,
                ttsTagIdsToRegenerate: ttsTagIdsToRegenerate ?? null,
              })
              .toPromise()
          : {
              data: {
                updateItem: { id: variables.id, ...variables.data },
              },
            };

        if (response?.error) {
          throw new Error(`Error in response: ${response.error}`);
        }

        const item =
          canSave("item")({ actions: data._actions, activeTasks }) &&
          response.data?.updateItem
            ? await convertToUIStructure(users)(programs)({
                ...response.data.updateItem,
              })
            : data;

        const optimisticItem =
          ttsTagIdsToRegenerate?.length && item
            ? {
                ...item,
                ttsTagStatuses: applyOptimisticRetryStatuses(
                  item.ttsTagStatuses,
                  ttsTagIdsToRegenerate,
                ),
              }
            : item;

        // Push the response onto the stream (even if nothing was returned, to make it clear that there was an error)
        subject.next({
          ...optimisticItem,
          tasks: updatedTasks,
        });
      } catch (err) {
        console.error(err);
        throw err;
      }
    },
  /**
   * Write data to the stream
   */
  set: (value) => {
    subject.next(value);
  },
  /**
   * Subscribe to the stream
   *
   * @returns {Function} A function to unsubscribe from the stream
   */
  subscribe: (setState) => {
    return subject.subscribe(setState);
  },
};

/**
 * Convert item data to what is expected by the graphQL API
 *
 * @param {Object} item
 * @returns { id:{string}, data: {Object}}
 */
const convertToAPIStructure =
  (users) =>
  (validTypes) =>
  ({ id, ...item }) => {
    const returnNameFromId = (array) => (val) => {
      return array.find((element) => element?.id === val)?.name;
    };
    const data = Object.keys(item).reduce((acc, key) => {
      // console.debug(`Converting item.${key}`, item[key]);
      switch (key) {
        case "_actions":
        case "useScreenReader":
        case "ttsTagStatuses":
          // Remove the value entirely
          return { ...acc };
        case "accessibilityElements":
          const [, accessibilityElements] = getTextToSpeechTags("item")(item);
          item.useTextToSpeech = accessibilityElements.length !== 0;
          item.lockFoilOrder = accessibilityElements.length !== 0;
          return {
            ...acc,
            [key]:
              accessibilityElements.length !== 0
                ? removeLabelfromTTS(accessibilityElements)
                : null,
          };
        case "content":
          const el = document.createElement("div");
          let scrReaderVal = item.useScreenReader;
          el.innerHTML = item.content;
          if (Array.from(el.querySelectorAll(".qti-visually-hidden")).length) {
            scrReaderVal = true;
          }
          if (!scrReaderVal) {
            [
              ...el.querySelectorAll('section[data-interaction-type="Choice"]'),
            ].map((interaction) => {
              const index = item.interactions.findIndex(
                ({ identifier }) =>
                  identifier ===
                  interaction.getAttribute("data-interaction-name"),
              );
              item.interactions[index]?.foils?.map((foil, i) => {
                const foilElement = document.createElement("div");
                foilElement.innerHTML = foil.content;
                if (
                  Array.from(
                    foilElement.querySelectorAll(".qti-visually-hidden"),
                  ).length
                ) {
                  scrReaderVal = true;
                }
              });
            });
          }
          unwrapElements(`o\\:p`)(el);
          return {
            ...acc,
            content: _.pipe([
              adjustTableFormattingForAPI,
              adjustMathMLSpacing,
              removeClassMsoNormalAttr,
              removeFontFamilyFromStyle,
              encodeMathMLTags,
              removeCdeIds,
              removeDivForInteractions,
              removeFontWeight400FromStyle,
              convertUTF8ToHTMLEntities, // Must be last, as DOM manipulation reconverts to UTF8
            ])(el.innerHTML),
            useScreenReader: scrReaderVal,
          };
        case "contentSpecialist":
        case "createdByUser":
        case "itemWriter":
        case "lastModifiedByUser":
          return {
            ...acc,
            [key]: !item[key]
              ? null
              : {
                  id: item[key],
                  name: returnNameFromId(users)(item[key]),
                },
          };
        case "contentComments":
        case "contentSuggestions":
          return { ...acc, [key]: JSON.stringify(item[key] || []) };
        case "copyrightDate":
          let value = null;
          if (item[key]) {
            // Return only the date part of the timestamp, and do it in a way that it won't mess with timezones
            const year = item[key].getFullYear();
            const month = ("0" + (item[key].getMonth() + 1)).substr(-2);
            const day = ("0" + item[key].getDate()).substr(-2);
            value = `${year}-${month}-${day}`;
          }
          return { ...acc, [key]: value };
        case "imageAssets":
          return {
            ...acc,
            [key]: Array.from(item[key] || []).map(
              ({
                altText = null,
                fileTimestamp,
                identifier = null,
                longDescription = null,
                longDescriptionDetails,
                name,
                printableAsset,
                user = null,
              }) => {
                return {
                  altText,
                  fileTimestamp,
                  identifier,
                  longDescription,
                  longDescriptionDetails: longDescriptionDetails
                    ? {
                        category: longDescriptionDetails.category,
                        content: longDescriptionDetails.content,
                        requestId: longDescriptionDetails.requestId,
                      }
                    : null,
                  name,
                  printableAsset: printableAsset
                    ? {
                        name: printableAsset.name,
                        fileTimestamp: printableAsset.fileTimestamp,
                        user: printableAsset.user,
                      }
                    : null,
                  user,
                };
              },
            ),
          };
        case "graphicAssets":
        case "mediaAssets":
        case "metafileAssets":
          return {
            ...acc,
            [key]: Array.from(item[key] || []).map(
              ({
                description,
                fileTimestamp,
                isASL,
                isPBT,
                name,
                user = null,
              }) => {
                return {
                  description,
                  fileTimestamp,
                  isASL,
                  isPBT,
                  name,
                  user,
                };
              },
            ),
          };
        case "interactions":
          // Strip 'id' from the interactions (being added by react-hook-form)
          const interactions = (Array.from(item[key]) || []).map(
            ({ id: rhfID, ...o }) => o,
          );
          let length = interactions.length;
          let oldType = item.type;
          let validType = "";

          // if current item type is invalid, default it to a valid value
          if (!validTypes.find((data) => data.value === item.type)) {
            if (length === 0) {
              validType = "Soft Materials without Interaction";
            } else if (length === 1) {
              const intType = item.interactions[0].__typename;
              switch (intType) {
                case "ChoiceInteraction":
                  validType = "Multiple Choice";
                  break;
                case "ConstructedResponse":
                case "ExtendedTextInteraction":
                  validType = "Constructed Response";
                  break;
                case "InlineChoiceInteraction":
                case "DemographicTextEntryInteraction":
                case "TextEntryInteraction":
                case "MatchInteraction":
                case "MatchTableInteraction":
                case "OrderInteraction":
                  validType = "Technology Enhanced";
                  break;
                default:
                  console.error("Unknown Interaction Type");
                  break;
              }
            } else {
              validType = "Composite";
            }
          }

          if (oldType !== validType && validType !== "") {
            item.type = validType;
          }

          const getExpectedLines = (expectedLength) => {
            if (!expectedLength) return null;
            if (expectedLength < 1000) return 4;
            if (expectedLength < 10000) return 10;
            return 35;
          };

          // Separate interactions into their separate type keys, adjust structures as needed
          return {
            ...acc,
            choiceInteractions: interactions
              .filter((o) => o.__typename === "ChoiceInteraction")
              .map(({ foils, ...interaction }) => ({
                ...interaction,
                foils: foils.map(
                  ({
                    content,
                    contentComments,
                    contentSuggestions,
                    correct,
                    distractorRationale,
                  }) => {
                    const ele = document.createElement("div");
                    ele.innerHTML = content;
                    unwrapElements(`o\\:p`)(ele);
                    const eleRationale = document.createElement("div");
                    eleRationale.innerHTML = distractorRationale;
                    unwrapElements(`o\\:p`)(eleRationale);
                    return {
                      content: _.pipe([
                        adjustTableFormattingForAPI,
                        adjustMathMLSpacing,
                        removeClassMsoNormalAttr,
                        removeFontFamilyFromStyle,
                        wrapPlainTextWithP,
                        encodeMathMLTags,
                        removeCdeIds,
                        removeFontWeight400FromStyle,
                        convertUTF8ToHTMLEntities, // Must be last, as DOM manipulation reconverts to UTF8
                      ])(ele.innerHTML),
                      contentComments: JSON.stringify(contentComments || []),
                      contentSuggestions: JSON.stringify(
                        contentSuggestions || [],
                      ),
                      correct,
                      distractorRationale: _.pipe([
                        removeXMLNSAttributesFromMathML,
                        removeClassMsoNormalAttr,
                        removeFontFamilyFromStyle,
                        wrapPlainTextWithP,
                        removeCdeIds,
                        removeFontWeight400FromStyle,
                        encodeMathMLTags,
                      ])(eleRationale.innerHTML),
                    };
                  },
                ),
              })),
            extendedTextInteractions: interactions
              .filter((o) => o.__typename === "ExtendedTextInteraction")
              .map((interaction) => ({
                ...interaction,
                expectedLines: getExpectedLines(interaction.expectedLength),
              })),
            gapMatchInteractions: interactions
              .filter((o) => o.__typename === "GapMatchInteraction")
              .map(({ elements, targets, ...interaction }) => ({
                ...interaction,
                elements: elements.map(
                  ({ content, identifier, matchGroup, matchMax }) => ({
                    content,
                    identifier,
                    matchGroup,
                    matchMax,
                  }),
                ),
                targets: targets.map(
                  ({ correctResponse, identifier, matchGroup, width }) => ({
                    correctResponse,
                    identifier,
                    matchGroup,
                    width,
                  }),
                ),
              })),
            graphicGapMatchInteractions: interactions
              .filter((o) => o.__typename === "GraphicGapMatchInteraction")
              .map(
                ({ correctResponses, elements, targets, ...interaction }) => ({
                  ...interaction,
                  correctResponses: correctResponses.map(
                    ({ element, target }) => ({ element, target }),
                  ),
                  elements: elements.map(
                    ({ identifier, imageUrl, matchMax }) => ({
                      identifier,
                      imageUrl,
                      matchMax,
                    }),
                  ),
                  targets: targets.map(
                    ({
                      height,
                      identifier,
                      matchMax,
                      width,
                      xCoord,
                      yCoord,
                    }) => ({
                      height,
                      identifier,
                      matchMax,
                      width,
                      xCoord,
                      yCoord,
                    }),
                  ),
                }),
              ),
            hotspotInteractions: interactions
              .filter((o) => o.__typename === "HotspotInteraction")
              .map(({ elements, ...interaction }) => ({
                ...interaction,
                elements: elements.map(
                  ({
                    colorStyle,
                    height,
                    identifier,
                    left,
                    radius,
                    shape,
                    top,
                    width,
                    xCoord,
                    yCoord,
                  }) => ({
                    colorStyle,
                    height,
                    identifier,
                    left,
                    radius,
                    shape,
                    top,
                    width,
                    xCoord,
                    yCoord,
                  }),
                ),
              })),
            inlineChoiceInteractions: interactions
              .filter((o) => o.__typename === "InlineChoiceInteraction")
              .map(({ choices, ...interaction }) => ({
                ...interaction,
                choices: choices.map(({ correct, text }) => {
                  const contentElement = document.createElement("div");
                  contentElement.innerHTML = text;
                  return {
                    correct,
                    text: contentElement.textContent,
                  };
                }),
              })),
            matchInteractions: interactions
              .filter((o) => o.__typename === "MatchInteraction")
              .map(
                ({ correctResponse, elements, targets, ...interaction }) => ({
                  ...interaction,
                  correctResponse: correctResponse.map(
                    ({ element, target }) => ({ element, target }),
                  ),
                  elements: elements.map(
                    ({ content, identifier, matchGroup, matchMax }) => ({
                      content,
                      identifier,
                      matchGroup,
                      matchMax,
                    }),
                  ),
                  targets: targets.map(
                    ({ identifier, matchGroup, matchMax, text, width }) => ({
                      identifier,
                      matchGroup,
                      matchMax,
                      text,
                      width,
                    }),
                  ),
                }),
              ),
            matchTableInteractions: interactions
              .filter((o) => o.__typename === "MatchTableInteraction")
              .map(({ columnChoices, rowChoices, ...interaction }) => ({
                ...interaction,
                columnChoices: columnChoices.map(
                  ({ content, identifier, matchMax, name }) => ({
                    content,
                    identifier,
                    matchMax,
                    name: name,
                  }),
                ),
                rowChoices: rowChoices.map(
                  ({ content, construct, identifier, matchMax, name }) => ({
                    content,
                    construct,
                    identifier,
                    matchMax,
                    name: name,
                  }),
                ),
              })),
            orderInteractions: interactions
              .filter((o) => o.__typename === "OrderInteraction")
              .map(({ correctResponse, elements, ...interaction }) => ({
                ...interaction,
                elements: elements.map(({ content, identifier }) => ({
                  content,
                  identifier,
                })),
                correctResponse: correctResponse.map(
                  (element) => element?.identifier,
                ),
              })),
            textEntryInteractions: interactions.filter(
              (o) => o.__typename === "TextEntryInteraction",
            ),
            demographicTextEntryInteractions: interactions.filter(
              (o) => o.__typename === "DemographicTextEntryInteraction",
            ),
          };
        case "passage":
          // Remove the value entirely
          return { ...acc };
        case "program":
          return { ...acc, [key]: item[key].id };
        case "referenceCitations":
          const convertedReferenceCitations = item[key]?.map(
            ({ wordCount, ...rest }) => {
              return {
                ...rest,
                wordCount: wordCount ? wordCount : null,
              };
            },
          );
          return {
            ...acc,
            [key]: convertedReferenceCitations,
          };
        case "relationships":
          const convertedRelationships = item[key]?.map(
            ({ program, source, type, ...rest }) => {
              const convertValue = (string) =>
                string.replace(/-|\s/g, "_").toUpperCase();
              return {
                ...rest,
                type: convertValue(type),
                source: convertValue(source),
              };
            },
          );
          return {
            ...acc,
            [key]:
              convertedRelationships && convertedRelationships.length
                ? uniqueObjects(convertedRelationships, [
                    "id",
                    "name",
                    "source",
                    "type",
                  ])
                : [],
          };
        case "release":
        case "restriction":
        case "standards":
          // if empty array change to null
          return { ...acc, [key]: item[key].length !== 0 ? item[key] : [] };
        case "wordCount":
          // Return 'null' if value is empty string
          return { ...acc, [key]: item[key] ? item[key] : null };
        default:
          // convert empty strings back to null values
          return { ...acc, [key]: item[key] ? item[key] : null };
      }
    }, {});
    return {
      id,
      data: {
        ...data,
        cbtCharacterCount: getCharacterCount("cbt")(data),
        hasUnresolvedChangeTags: getHasUnresolvedChangeTags(data),
        pbtCharacterCount: getCharacterCount("pbt")(data),
      },
    };
  };

/**
 * Convert item data to what is expected by the UI
 *
 * @param {Object} item
 * @returns {Object}
 */
const convertToUIStructure = (users) => (programs) => async (item) => {
  // console.debug("Calling convertToUIStructure with item %o", item);
  const program = programs.find((o) => item?.program === o.id);
  const imageAssets = item?.imageAssets ? item.imageAssets : [];
  const graphicAssets = item?.graphicAssets ? item.graphicAssets : [];
  const mediaAssets = item?.mediaAssets ? item.mediaAssets : [];
  const metafileAssets = item?.metafileAssets ? item.metafileAssets : [];
  const programMetafileAssets =
    program?.metafileAssets?.filter((asset) =>
      asset?.itemAssociations?.includes(item?.id),
    ) || [];

  const promises = [
    ...imageAssets,
    ...graphicAssets,
    ...mediaAssets,
    ...metafileAssets,
    ...programMetafileAssets,
  ].map((asset) => {
    const objectID =
      asset?.__typename === "ProgramMetafileAsset" ? program?.id : item?.id;
    return getAssetInfo(objectID)(asset);
  });
  return Promise.all(promises)
    .then((assets) => {
      const convertedImageAssets = assets.filter(
        (asset) => asset.__typename === "ImageAsset",
      );
      const convertedAssets = (type) =>
        assets.filter((asset) => asset.__typename === type);
      console.info("Got item assets %o", assets);
      return Object.keys(item).reduce((acc, key) => {
        switch (key) {
          case "accessibilityElements":
          case "release":
          case "restriction":
          case "standards":
            // convert null to empty array
            return { ...acc, [key]: item[key] ? item[key] : [] };
          case "choiceInteractions":
          case "extendedTextInteractions":
          case "gapMatchInteractions":
          case "graphicGapMatchInteractions":
          case "inlineChoiceInteractions":
          case "matchInteractions":
          case "matchTableInteractions":
          case "orderInteractions":
          case "textEntryInteractions":
          case "hotspotInteractions":
          case "demographicTextEntryInteractions":
            const interactions = acc.interactions ? acc.interactions : [];
            const newInteractions = item[key]
              ? item[key].map((obj) => {
                  switch (obj.__typename) {
                    case "ChoiceInteraction":
                      return {
                        ...obj,
                        foils: obj?.foils?.map(
                          ({
                            content,
                            contentComments,
                            contentSuggestions,
                            distractorRationale,
                            ...foil
                          }) => ({
                            ...foil,
                            content: _.pipe([
                              removePTagFromFoils,
                              removeMarkDataSuggestion(contentSuggestions),
                              adjustTableFormattingForUI,
                              encodeMathMLTags,
                              convertUTF8ToHTMLEntities, // Must be last, as DOM manipulation reconverts to UTF8
                            ])(content),
                            contentComments: contentComments
                              ? JSON.parse(contentComments)
                              : [],
                            contentSuggestions: contentSuggestions
                              ? JSON.parse(contentSuggestions)
                              : [],
                            distractorRationale: _.pipe([removePTagFromFoils])(
                              distractorRationale,
                            ),
                          }),
                        ),
                      };
                    case "InlineChoiceInteraction":
                      // Create paragraph tags for the text content
                      return {
                        ...obj,
                        choices: obj.choices.map(({ text, ...choice }) => {
                          const contentElement = document.createElement("p");
                          contentElement.textContent = text;
                          return { ...choice, text: contentElement.outerHTML };
                        }),
                      };
                    case "OrderInteraction":
                      // add identifier for RHF
                      return {
                        ...obj,
                        correctResponse: obj.correctResponse.map((cr) => ({
                          identifier: cr,
                        })),
                      };
                    default:
                      return obj;
                  }
                })
              : [];

            return {
              ...acc,
              interactions: [...interactions, ...newInteractions],
            };
          case "content":
            return {
              ...acc,
              [key]: _.pipe([
                removeNewlineAndTab,
                removeMarkDataSuggestion(item?.contentSuggestions),
                adjustTableFormattingForUI,
                encodeMathMLTags,
                convertUTF8ToHTMLEntities, // Must be last, as DOM manipulation reconverts to UTF8
              ])(item[key]),
            };
          case "contentSpecialist":
          case "createdByUser":
          case "itemWriter":
          case "lastModifiedByUser":
            return {
              ...acc,
              [key]: item[key]?.id,
            };
          case "contentComments":
          case "contentSuggestions":
            return {
              ...acc,
              [key]: item[key] ? JSON.parse(item[key]) : [],
            };
          case "copyrightDate":
            // Return only the date part of the timestamp
            return {
              ...acc,
              [key]: item[key]
                ? new Date(Date.parse(`${item[key]}T00:00:00`))
                : null,
            };
          case "firstBankedDateTime":
          case "lastBankedDateTime":
          case "createdDateTime":
          case "lastModifiedDateTime":
            return { ...acc, [key]: item[key] ? new Date(item[key]) : null };
          case "graphicAssets":
            return { ...acc, [key]: convertedAssets("GraphicAsset") };
          case "imageAssets":
            return { ...acc, [key]: convertedImageAssets };
          case "mediaAssets":
            return { ...acc, [key]: convertedAssets("MediaAsset") };
          case "metafileAssets":
            return { ...acc, [key]: convertedAssets("MetafileAsset") };
          case "program":
            const { id, name, configurationID } = program;
            return {
              ...acc,
              [key]: {
                id,
                name,
                configurationID,
                metafileAssets: convertedAssets("ProgramMetafileAsset"),
              },
            };
          case "relationships":
            // get relationship programs and convert values for metadata table
            const convertedRelationships = item[key]?.map(
              ({ program, type, source, ...rest }) => {
                const programName = programs.find((o) => program === o?.id);
                const convertOptions = (options) => (val) =>
                  options.find(
                    (option) =>
                      val === option.replace(/-|\s/g, "_").toUpperCase(),
                  );
                return {
                  ...rest,
                  program: programName?.name,
                  source: convertOptions(dataSourceOptions)(source),
                  type: convertOptions(relationshipTypeOptions)(type),
                };
              },
            );
            return { ...acc, [key]: convertedRelationships || [] };
          case "referenceCitations":
            const sortedRefs = _.sortBy("referenceTitle")(item[key]);
            return { ...acc, [key]: item[key] ? sortedRefs : [] };
          default:
            // convert null values to empty strings
            return { ...acc, [key]: item[key] ? item[key] : "" };
        }
      }, {});
    })
    .catch((err) => {
      console.error(err);
      throw new Error(err);
    });
};

const itemFields = `
  _actions
  accessibilityElements {
    accessibilityFeatures {
      feature
      language
      src
      text
      type
    }
    identifier
    inclusionOrders
  }
  ttsTagStatuses {
    tagId
    status
  }
  aiContent
  application
  careerCluster
  cbtCharacterCount
  cdeID
  choiceInteractions {
    foils {
      content
      contentComments
      contentSuggestions
      correct
      distractorRationale
    }
    identifier
    maxChoices
    minChoices
  }
  cognitiveCode
  content
  contentArea
  contentComments
  contentLicensed
  contentSpecialist {
    id
    name
  } 
  contentSuggestions
  copyrightCertificate
  copyrightDate
  createdByUser {
    id
    name
  }
  createdDateTime
  demographicTextEntryInteractions {
    correctResponse
    expectedLength
    identifier
    ignoreCase
    patternMask
    placeholderText
  }
  depthOfKnowledge
  description
  developmentState
  difficulty
  elementStatus
  ethnicity
  extendedTextInteractions {
    expectedLength
    expectedLines
    identifier
    scoringCriteria
  }
  externalID
  firstBankedDateTime
  foreignID
  format
  gapMatchInteractions {
    elements {
      content
      identifier
      matchGroup
      matchMax
    }
    identifier
    targets {
      correctResponse
      identifier
      matchGroup
      width
    }
  }
  graphicGapMatchInteractions {
    backgroundFileName
    backgroundHeight
    backgroundWidth
    correctResponses {
      element
      target
    }
    elements {
      identifier
      imageUrl
      matchMax
    }
    identifier
    targets {
      height
      identifier
      matchMax
      width
      xCoord
      yCoord
    }
  }
  gender
  genderRole1
  genderRole2
  genderRole3
  generality
  gradeLevel
  gradeSpanEnd
  gradeSpanStart
  graphicComplexity
  gradeSpanEnd
  gradeSpanStart
  graphicAssets {
    fileTimestamp
    name
    user
  }
  groupRole1
  groupRole2
  groupRole3
  hasUnresolvedChangeTags
  hotspotInteractions {
    backgroundFileName
    backgroundHeight
    backgroundWidth
    elements {
      colorStyle
      correctResponse
      height
      identifier
      left
      radius
      shape
      top
      width
      xCoord
      yCoord
    }
    identifier
    maxChoices
  }
  id
  idealPretestLocation
  importID
  imageAssets {
    altText
    fileTimestamp
    identifier
    longDescription
    longDescriptionDetails {
      category
      content
      requestId
    }
    name
    user
    printableAsset {
      fileTimestamp
      name
      user
    }
  }
  income
  inlineChoiceInteractions {
    choices {
      correct
      text
    }
    distractorRationale
    identifier
  }
  itemBasedOnPassage
  itemLevel
  itemModel
  itemWriter {
    id
    name
  }
  jeLevel
  language
  lastBankedDateTime
  lastModifiedByUser {
    id
    name
  }
  lastModifiedDateTime
  lockFoilOrder
  matchInteractions {
    correctResponse {
      element
      target
    }
    elements {
      content
      identifier
      matchGroup
      matchMax
    }
    identifier
    maxElementsPerRow
    targets {
      identifier
      matchGroup
      matchMax
      text
      width
    }
  }
  matchTableInteractions { 
    class                     
    identifier                
    maxAssociations           
    minAssociations           
    prompt                    
    rowCentric
    shuffle
    templateName
    columnChoices {         
      content                 
      fixed
      identifier
      matchMax
      matchMin
      showHide
      name
    }
    rowChoices {
      content
      construct
      fixed
      identifier
      matchMax
      matchMin
      showHide
      name
    }
  }
  mathTaxonomyCode
  mathTaxonomyList
  mediaAssets {
    description
    fileTimestamp
    name
    user
  }
  memo
  metafileAssets {
    description
    fileTimestamp
    isASL
    isPBT
    name
    user
  }
  modeling
  name
  orderInteractions {
    correctResponse
    elements {
      content
      identifier
    }
    identifier
    orientation
  }
  partialScore
  passageAsAWhole
  passthrough
  pbtCharacterCount
  points
  pretestGroup
  primaryContentCode
  program
  readabilityIndex
  region
  relationships {
    id
    name
    program
    source
    type
  }
  release
  religion
  referenceCitations {
    author
    citation
    commissioned
    copyrightYear
    ISBN
    notes
    page
    publicationInformation
    publicDomain
    publisher
    referenceTitle
    rightsHolder
    sourceURL
    wordCount
    workTitle 
  }
  resource
  restriction
  secondaryContentCode
  secondaryDOK
  scoringMethod
  skillSubdomain
  snapToHighlight
  standards {
    humanCodingScheme
    order
  }
  subEthnicity
  subProgram
  subRegion
  tertiaryDOK
  textComplexity
  textEntryInteractions {
    correctResponse
    expectedLength
    identifier
    ignoreCase
    patternMask
    placeholderText
  }
  type
  uct
  unavailability
  universalDesign
  urbanicity
  useScreenReader
  useTextToSpeech
  versionID
  wordCount
`;

const lightweightItemFields = `
    _actions
    aiContent
    application
    careerCluster
    cbtCharacterCount
    cognitiveCode
    contentArea
    contentLicensed
    contentSpecialist {
      id
      name
      __typename
    }
    copyrightCertificate
    copyrightDate
    createdByUser {
      id
      name
      __typename
    }
    createdDateTime
    depthOfKnowledge
    description
    developmentState
    difficulty
    elementStatus
    ethnicity
    externalID
    firstBankedDateTime
    foreignID
    format
    gender
    genderRole1
    genderRole2
    genderRole3
    generality
    gradeLevel
    gradeSpanEnd
    gradeSpanStart
    graphicComplexity
    gradeSpanEnd
    gradeSpanStart
    groupRole1
    groupRole2
    groupRole3
    id
    idealPretestLocation
    importID
    income
    itemBasedOnPassage
    itemLevel
    itemModel
    itemWriter {
      id
      name
      __typename
    }
    jeLevel
    language
    lastBankedDateTime
    lastModifiedByUser {
      id
      name
      __typename
    }
    lastModifiedDateTime
    lockFoilOrder
    mathTaxonomyCode
    mathTaxonomyList
    memo
    modeling
    name
    partialScore
    passageAsAWhole
    passthrough
    pbtCharacterCount
    points
    pretestGroup
    primaryContentCode
    program
    readabilityIndex
    region
    relationships {
      id
      name
      program
      source
      type
      __typename
    }
    release
    religion
    resource
    restriction
    secondaryContentCode
    secondaryDOK
    scoringMethod
    skillSubdomain
    snapToHighlight
    standards {
      humanCodingScheme
      order
      __typename
    }
    subEthnicity
    subProgram
    subRegion
    tertiaryDOK
    textComplexity
    type
    uct
    unavailability
    universalDesign
    urbanicity
    useScreenReader
    useTextToSpeech
    versionID
    wordCount
`;

const getItemQuery = `
  query GetItem($id: ID!) {
    itemByID(id: $id) { ${itemFields} }
  }`;

const updateItemMutation = `
  mutation UpdateItem($id: ID!, $data: ItemInput!, $notes: String, $ttsGenerate: Boolean!, $ttsTagIdsToRegenerate: [String]) {
    updateItem(id: $id, data: $data, notes: $notes, ttsGenerate: $ttsGenerate, ttsTagIdsToRegenerate: $ttsTagIdsToRegenerate) { ${itemFields} }
  }`;

export {
  convertToAPIStructure,
  convertToUIStructure,
  getItemQuery,
  itemFields,
  lightweightItemFields,
  store,
  updateItemMutation,
};
